import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ButtonModal } from '../ButtonModal';
import { Modal } from 'react-bootstrap';
import { CloseButton } from '../../styles';
import { IoClose } from 'react-icons/io5';
import { FormCadastroRapido } from '../FormCadastroRapido';
import { FaRegClone } from 'react-icons/fa';
import { Tooltip } from '@material-ui/core';
import { FormClonarProduto } from '../FormClonarProduto';
import { LojaContext } from '~/context/loja';
import {
  ProdutoClonado,
  SessionStorageService,
} from '../../class/SessionStorageService';
import { validaGtin } from '~/utils/functions';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import api from '~/services/api';
import { focusAndClickElement } from '../../utils/focusAndClickElement';
import { geradorDePLU } from '../../utils/geradorDePLU';

interface ModalCadastroRapidoProps {
  codTela: number;
  firstModalTitle: string;
  isShow: boolean;
  onHide: () => void;
  setIsCadastroRapidoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCadastroRapidoOpen: boolean;
  onSuccess?: (novoProduto: any) => void;
}

export const ModalCadastroRapido: React.FC<ModalCadastroRapidoProps> = ({
  codTela,
  firstModalTitle,
  isShow,
  onHide,
  setIsCadastroRapidoOpen,
  isCadastroRapidoOpen,
  onSuccess = undefined,
}) => {
  const { loja } = useContext(LojaContext);
  const [isModalClonarOpen, setIsModalClonarOpen] = useState(false);
  const [lojaSelected, setLojaSelected] = useState<
    number | number[] | undefined
  >(undefined);
  const [produtoClonado, setProdutoClonado] = useState<ProdutoClonado | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    control,
    getValues,
    setValue,
    formState,
    clearErrors,
    setError,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const abrirModalClonar = () => {
    setIsModalClonarOpen(true);
    setIsCadastroRapidoOpen(false);
    setTimeout(() => {
      focusAndClickElement('produto_base');
    }, 250);
  };

  const onHideClonar = useCallback(() => {
    setIsModalClonarOpen(false);
    setIsCadastroRapidoOpen(true);
  }, [setIsCadastroRapidoOpen]);

  const handleLoja = useCallback((codLoja?: number | number[]) => {
    setLojaSelected(codLoja);
  }, []);

  const onCancelProdutoClone = useCallback(() => {
    setProdutoClonado(null);

    setValue('val_venda', '0,00');
    setValue('descricao', '');
    setValue('qtd_estoque', '1');
    setValue('cod_departamento', { value: null, label: '' });
    setValue('cod_ncm', { value: null, label: '' });

    SessionStorageService.deleteProduto('produto-clonado', codTela);
    sessionStorage.removeItem(`ncm-${codTela}`);

    clearErrors();
  }, [clearErrors, codTela, setValue]);

  useEffect(() => {
    if (!isModalClonarOpen) {
      const produto = SessionStorageService.getProduto(
        'produto-clonado',
        codTela,
      );
      setProdutoClonado(produto);
    }
  }, [codTela, isModalClonarOpen]);

  useEffect(() => {
    if (!isCadastroRapidoOpen && !isModalClonarOpen) {
      onCancelProdutoClone();
      setValue('gtin', '');
      setValue('qtd_estoque', 1);
      setValue('cod_departamento', { value: null, label: '' });
    }
  }, [isCadastroRapidoOpen, isModalClonarOpen, onCancelProdutoClone, setValue]);

  const handleCadastrar = useCallback(async () => {
    if (!isShow || isModalClonarOpen) return;

    const gtin = String(getValues('gtin'));
    const descricao = getValues('descricao')?.toUpperCase()?.trim();
    const codDepartamento = getValues('cod_departamento')?.value;
    const codNcm = getValues('cod_ncm')?.value;
    const codProduto = String(getValues('cod_produto'));
    const valVenda = getValues('val_venda');
    const qtdEstoque = getValues('qtd_estoque');

    const inputGtin = document.querySelector(
      '[name="gtin"]',
    ) as HTMLInputElement | null;
    const gtinValid = validaGtin(gtin, gtin.length);

    if (gtin.length > 0 && !gtinValid.valid) {
      toast.warning(gtinValid.message);
      inputGtin?.focus();
      inputGtin?.click();
      return;
    }

    const isClonagem =
      !!produtoClonado && !!produtoClonado.p_cod_produto_origem;

    if (!isClonagem) {
      if (!lojaSelected) return toast.warning('Loja deve ser informada');

      if (!descricao) {
        setError('descricao', { type: 'required' });
        return toast.warning('Descrição deve ser informada');
      }

      if (!codDepartamento) {
        clearErrors('descricao');
        setError('cod_departamento', { type: 'required' });
        return toast.warning('Departamento deve ser informado');
      }

      if (!codNcm) {
        clearErrors('cod_departamento');
        setError('cod_ncm', { type: 'required' });
        return toast.warning('NCM deve ser informado');
      }
    }

    if (isClonagem && !descricao) {
      setError('descricao', { type: 'required' });
      return toast.warning('Descrição deve ser informada');
    }

    if (parseFloat(qtdEstoque) <= 0) {
      setError('qtd_estoque', { type: 'required' });
      return toast.warning('O estoque deve ser positivo');
    }

    const payload = {
      p_cod_loja: lojaSelected,
      p_cod_produto_origem: isClonagem
        ? produtoClonado.p_cod_produto_origem
        : null,
      p_cod_produto_destino: codProduto,
      p_gtin: gtinValid.valid && gtin.length > 0 ? gtin : null,
      p_descricao: descricao ?? null,
      p_cod_departamento: codDepartamento ?? null,
      p_cod_ncm: codNcm ?? null,
      p_val_venda: valVenda ?? null,
      p_qtd_estoque: parseFloat(qtdEstoque) ?? null,
    };

    try {
      setIsLoading(true);
      const { data: novoProduto } = await api.post(
        '/produto/cadastro-rapido',
        payload,
      );

      clearErrors();
      onHide();

      if (onSuccess && novoProduto.success) onSuccess(novoProduto.data);

      toast.success('Cadastro realizado com sucesso');
    } catch (error: any) {
      console.error('Erro ao cadastrar produto:', error);
      if (
        error?.data?.message?.includes(
          'Descrição do novo produto deve ser diferente do produto base.',
        ) &&
        produtoClonado
      ) {
        setError('descricao', { type: 'required' });
        focusAndClickElement('descricao');
      }

      if (
        error?.data?.message?.includes(
          'PLU duplicado! O código informado já existe no sistema',
        )
      ) {
        const plu = await geradorDePLU(codTela);
        setValue('cod_produto', plu);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    getValues,
    isModalClonarOpen,
    isShow,
    lojaSelected,
    onHide,
    produtoClonado,
    setError,
    clearErrors,
    onSuccess,
    codTela,
    setValue,
  ]);

  return (
    <>
      {isCadastroRapidoOpen && !isModalClonarOpen && (
        <Modal show={isShow} onHide={onHide} size="lg" centered>
          <Modal.Header>
            <Modal.Title>{firstModalTitle}</Modal.Title>
            <CloseButton onClick={onHide} aria-label="Fechar modal">
              <IoClose size={23} />
            </CloseButton>
          </Modal.Header>
          <Modal.Body>
            <FormCadastroRapido
              buttonClonar={
                <Tooltip title="Clonar produto existente">
                  <button type="button" onClick={abrirModalClonar}>
                    <FaRegClone size={18} />
                  </button>
                </Tooltip>
              }
              loja={loja}
              handleLoja={handleLoja}
              lojaSelected={lojaSelected}
              produtoClonado={produtoClonado}
              onCancelTableProdutoOrigem={onCancelProdutoClone}
              codTela={codTela}
              register={register}
              control={control}
              getValues={getValues}
              setValue={setValue}
              formState={formState}
              clearErrors={clearErrors}
            />
          </Modal.Body>

          <Modal.Footer>
            <ButtonModal
              onClick={async () => {
                await handleCadastrar();
              }}
              style={{ backgroundColor: '#009e0f' }}
              disabled={isLoading}
            >
              {isLoading ? 'CADASTRANDO...' : 'CADASTRAR'}
            </ButtonModal>
          </Modal.Footer>
        </Modal>
      )}

      {!isCadastroRapidoOpen && isModalClonarOpen && (
        <Modal
          show={isModalClonarOpen}
          onHide={onHideClonar}
          centered
          size="lg"
        >
          <Modal.Header>
            <Modal.Title>Clonar Produto</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormClonarProduto
              codTela={codTela}
              onHide={onHideClonar}
              lojaSelected={lojaSelected}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};
